import React, { useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { makeStyles } from '@mui/styles';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { PasswordInput } from '../../common';
import routes from '../../../utils/variables/routes';
import {
    validateCredentials,
    setAuth,
    triggerRecaptcha,
    resetAuth,
} from '../../../actions/auth';
import { isEmail, parseBool, uuidv4 } from '../../../utils/functions';
import { statusOk, disableRecaptchaEnv } from '../../../utils/variables';
import { ReCaptcha, Spinner } from '../../common';
import ServerErrorModal from '../serverError';
import styles from './style';

const useStyles = makeStyles(styles);

/**
 * LoginToContinueModal
 * Modal dialog shown when an unauthenticated user clicks "Purchase Now" on a product card.
 * Provides three options:
 *   1. Login — redirects to the sign-in page with the purchase URL as a redirect param
 *   2. Create account — redirects to the sign-up page
 *   3. Continue as Guest — closes the modal and navigates directly to the purchase wizard
 *
 * Props:
 *   open          — controls modal visibility
 *   onClose       — callback to close the modal without action
 *   onContinueAsGuest — callback to bypass login and proceed to the purchase wizard
 *   redirectUrl   — the purchase wizard URL; appended to the sign-in route so the user
 *                    is redirected back after successful authentication
 */
const LoginToContinueModal = ({
    open,
    onClose,
    onContinueAsGuest,
    redirectUrl,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    // Recaptcha and credential validation state from Redux
    // Used when inline login form is enabled (currently commented out)
    const siteWide = useSelector(state => state?.locations?.siteWide);
    const disableRecaptcha = siteWide?.disableRecaptcha || parseBool(disableRecaptchaEnv);
    const recaptchaToken = useSelector(state => state.auth.recaptchaToken);
    const recaptchaTriggered = useSelector(state => state.auth.triggerRecaptcha);
    const isValidateCredentialsPending = useSelector(
        state => state.auth.isValidateCredentialsPending,
    );
    const isValidateCredentialsSuccess = useSelector(
        state => state.auth.isValidateCredentialsSuccess,
    );
    const isValidateCredentialsError = useSelector(
        state => state.auth.isValidateCredentialsError,
    );
    const validateCredentialsResponse = useSelector(
        state => state.auth.validateCredentialsResponse,
    );
    const validateCredentialsError = useSelector(
        state => state.auth.validateCredentialsError,
    );
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const territory = useSelector(state => state?.locations?.yourLocation?.alpha3);
    const devices = useSelector(state => state?.disk?.devices || state.misc?.devices);

    const { register, handleSubmit, errors } = useForm({
        defaultValues: {
            username: '',
            password: '',
        },
    });

    // Holds form data between reCAPTCHA trigger and token arrival
    const [formDataToSubmit, setFormDataToSubmit] = React.useState(null);

    // Match the entered username against known devices to retrieve the device token
    // for multi-device authentication support
    const checkUsername = checkName => {
        const match = devices?.find(({ username, otherUsernames = [] }) => {
            if (otherUsernames && otherUsernames?.length > 0) {
                let matched = false;
                otherUsernames.forEach(otherName => {
                    if (isEmail(checkName)) {
                        if (otherName?.toLowerCase() === checkName?.toLowerCase()) {
                            matched = true;
                        }
                    } else if (otherName?.includes(checkName)) {
                        matched = true;
                    }
                });
                return matched;
            }
            if (isEmail(checkName)) {
                if (username?.toLowerCase() === checkName?.toLowerCase()) return true;
            } else if (username?.includes(checkName)) {
                return true;
            }
            return username?.toLowerCase() === checkName?.toLowerCase();
        });

        return match || {};
    };

    // Auto-close the modal if the user becomes authenticated while it's open
    // (e.g. after a successful inline login)
    useEffect(() => {
        if (isAuthenticated && open) {
            onClose();
        }
    }, [isAuthenticated, open, onClose]);

    // Reset auth state and refresh reCAPTCHA token when navigating away from the modal
    const closeDialog = () => {
        dispatch(resetAuth.resetAuthAction());
        dispatch(triggerRecaptcha.triggerRecaptchaAction(uuidv4()));
        setFormDataToSubmit(null);
    };

    // Submit credentials with reCAPTCHA token and device info for inline login
    const onSubmit = data => {
        const body = { redirect: redirectUrl || null };
        const matched = checkUsername(data?.username);
        const payload = {
            ...data,
            territory,
            deviceToken: matched?.deviceToken,
            redirect: redirectUrl || null,
        };

        dispatch(setAuth.setAuthUsernameAction(payload?.username));

        if (recaptchaToken || disableRecaptcha) {
            let headers = {};
            if (recaptchaToken) {
                headers = { 'captcha-response-token': recaptchaToken };
            }
            dispatch(validateCredentials.validateCredentialsAction(payload, body, headers));
        }
    };

    // If reCAPTCHA is disabled, submit directly; otherwise trigger reCAPTCHA first
    // and wait for the token before submitting
    const beforeSubmit = data => {
        if (disableRecaptcha) {
            onSubmit(data);
        } else {
            setFormDataToSubmit(data);
            dispatch(triggerRecaptcha.triggerRecaptchaAction(uuidv4()));
        }
    };

    // Once reCAPTCHA resolves and we have a token, submit the stored form data
    useEffect(() => {
        if (recaptchaTriggered && formDataToSubmit && recaptchaToken) {
            onSubmit(formDataToSubmit);
            setFormDataToSubmit(null);
        }
    }, [recaptchaToken]);

    // Determine if the credential validation returned an error response
    // to display the server error modal with the appropriate message
    const isValidateCredentialsResponseTypeError =
        validateCredentialsResponse?.responseType !== statusOk || false;

    let errorData = {};
    if (isValidateCredentialsError) {
        errorData = validateCredentialsError;
    } else if (isValidateCredentialsResponseTypeError && isValidateCredentialsSuccess) {
        errorData = validateCredentialsResponse;
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ className: classes.dialogPaper }}
        >
            <DialogContent>
                <Box className={classes.header}>
                    <Typography className={classes.title}>Login to Continue</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box className={classes.infoBox}>
                    <LightbulbOutlinedIcon fontSize="small" />
                    <Typography variant="body2">
                        Sign in to easily track your policy and claims later.
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit(beforeSubmit)}>
                    {/* <Typography className={classes.sectionLabel}>Mobile or Email</Typography> */}
                    {/* <TextField
                        inputRef={register({
                            required: 'Mobile or Email is required',
                        })}
                        name="username"
                        variant="outlined"
                        placeholder="Enter Mobile Number or Email Address"
                        fullWidth
                        className={classes.formField}
                        error={!!errors?.username}
                        helperText={errors?.username?.message}
                    /> */}

                    {/* <Typography className={classes.sectionLabel}>Password</Typography> */}
                    {/* <PasswordInput
                        inputRef={register({
                            required: 'Password is required',
                        })}
                        name="password"
                        variant="outlined"
                        placeholder="Enter Password"
                        fullWidth
                        className={classes.formField}
                        error={!!errors?.password}
                        helperText={errors?.password?.message}
                    /> */}

                    {isValidateCredentialsPending ? (
                        <Spinner loading message="Signing In" />
                    ) : (
                        <Button
                            type="button"
                            component={RouterLink}
                            to={
                                redirectUrl
                                    ? `${routes.auth.signIn}?redirect=${encodeURIComponent(
                                        redirectUrl,
                                    )}`
                                    : routes.auth.signIn
                            }
                            className={classes.primaryButton}
                            sx={{ width: '100%', mt: 1 }}
                            onClick={closeDialog}
                        >
                            Login
                        </Button>
                    )}

                    <Box className={classes.dividerRow}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="body2">or</Typography>
                        <Divider sx={{ flex: 1 }} />
                    </Box>

                    <Typography className={classes.helperText}>
                        New here? Create an account
                    </Typography>

                    <Button
                        component={RouterLink}
                        to={routes.auth.signUpBio}
                        className={classes.secondaryButton}
                        sx={{ width: '100%', mt: 1 }}
                        onClick={closeDialog}
                    >
                        Create account
                    </Button>

                    <Button
                        type="button"
                        className={classes.tertiaryButton}
                        sx={{ width: '100%', mt: 1.5, textTransform: 'none' }}
                        onClick={onContinueAsGuest}
                    >
                        Continue as Guest
                    </Button>
                </form>

                <ServerErrorModal
                    open={
                        isValidateCredentialsError ||
                        (isValidateCredentialsSuccess && isValidateCredentialsResponseTypeError)
                    }
                    responseData={errorData}
                    closeDialog={closeDialog}
                    alertColor="error"
                    redirectPath={routes.home}
                />
                <ReCaptcha recaptchaType="invisible" />
            </DialogContent>
        </Dialog>
    );
};

export default LoginToContinueModal;
